/**
 * One definition each for what the client console's tiles report.
 *
 * WHY THIS FILE EXISTS. These derivations used to live inside the seven
 * `Client*Display` components, which was fine while all seven were mounted at
 * once. In the console a section only mounts when its tile is selected — but
 * the TILE has to report before that. Writing them inline in `SingleClient`
 * would fork every one of them, and "what counts as an active quote" living in
 * two places is a drift bug with a date on it.
 *
 * ─────────────────────────────────────────────────────────────────
 * THE RULE: A TILE REPORTS A STATE, NOT A SIZE.
 *
 * "6 invoices" is noise — it does not tell you whether to click. "2 unpaid"
 * does. So a plain count is carried ONLY by the two sections whose items have
 * no state to be in:
 *
 *   count, no status    Client Notes, Client Photos
 *   status only         Emails, Messages, Tasks, Invoices, Quotes
 *   neither             Activity (no resolver yet)
 *
 * A section in the second group deliberately reports NOTHING when everything
 * is in order. A tile with no badge means "nothing here needs you", which is
 * information — and it is why those return `count: undefined` rather than a
 * total nobody asked for.
 * ─────────────────────────────────────────────────────────────────
 *
 * Each returns `{ count, statuses }`:
 *   count     number, or `undefined` for "no count is reported". For Notes and
 *             Photos `undefined` also means "not known yet", and is NOT
 *             interchangeable with `0` — zero renders the empty treatment and
 *             claims the section is empty, which is a lie for a list that has
 *             not loaded.
 *   statuses  an ARRAY of `{ tone, value, text }`, most urgent first, empty
 *             when nothing needs attention. An array because Emails reports up
 *             to three at once. `value` is the bare number the tile shows,
 *             `text` the phrase the VS panel header shows. Tones are
 *             `danger` | `warning` | `accent` — the vocabulary BdrDisclosure's
 *             TONE_CLASS already uses.
 */

const NONE = { count: undefined, statuses: [] };

/* ── the two that carry a plain count ─────────────────────────── */

/** Notes have no state. A note is just a note. */
export const noteTile = (notesArr) =>
  Array.isArray(notesArr)
    ? { count: notesArr.length, statuses: [] }
    : { ...NONE };

/**
 * Photos likewise. The IDs arrive with the client, so the count is known
 * before the section has ever been mounted.
 */
export const photoTile = (photoIdsArr) =>
  Array.isArray(photoIdsArr)
    ? { count: photoIdsArr.length, statuses: [] }
    : { ...NONE };

/* ── the ones that report only a state ────────────────────────── */

/** Money still owed is the only thing worth reporting on an invoices tile. */
export const invoiceTile = (invoicesArr) => {
  if (!Array.isArray(invoicesArr)) return { ...NONE };
  const unpaid = invoicesArr.filter((invoice) => !invoice?.invoicePaid).length;
  return {
    count: undefined,
    statuses:
      unpaid > 0
        ? [{ tone: "warning", value: unpaid, text: `${unpaid} unpaid` }]
        : [],
  };
};

/**
 * An active quote is a live opportunity; invoiced and expired ones are history.
 * The `/active/gi` test matches what ClientQuotesDisplay's bullet colour uses —
 * change one and change the other, or a quote can be amber in the list and
 * uncounted on the tile.
 */
export const quoteTile = (quotesArr) => {
  if (!Array.isArray(quotesArr)) return { ...NONE };
  const active = quotesArr.filter((quote) =>
    /active/gi.test(quote?.quoteStatus)
  ).length;
  return {
    count: undefined,
    statuses:
      active > 0
        ? [{ tone: "accent", value: active, text: `${active} active` }]
        : [],
  };
};

/**
 * Overdue = due before the start of today. Built by (year, month, day)
 * construction rather than ms arithmetic, so a DST boundary inside the range
 * cannot shift the cutoff off its intended day (CLAUDE.md, DateTime safety).
 */
export const taskTile = (tasksArr) => {
  if (!Array.isArray(tasksArr)) return { ...NONE };

  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  ).getTime();

  const overdue = tasksArr.filter((task) => {
    const due = Number(task?.dueDate);
    return Number.isFinite(due) && due > 0 && due < startOfToday;
  }).length;

  return {
    count: undefined,
    statuses:
      overdue > 0
        ? [{ tone: "danger", value: overdue, text: `${overdue} overdue` }]
        : [],
  };
};

/**
 * `unreadMessages` is a per-phone boolean, so this counts THREADS holding
 * unread mail rather than individual texts — saying "2 unread" when it means
 * two threads would be wrong, hence the wording.
 */
export const messageTile = (phoneNumberIds) => {
  if (!Array.isArray(phoneNumberIds)) return { ...NONE };

  const unreadThreads = phoneNumberIds.filter(
    (phone) => phone?.unreadMessages === true
  ).length;

  return {
    count: undefined,
    statuses:
      unreadThreads > 0
        ? [
            {
              tone: "accent",
              value: unreadThreads,
              text:
                unreadThreads === 1
                  ? "1 unread thread"
                  : `${unreadThreads} unread threads`,
            },
          ]
        : [],
  };
};

/**
 * The only section reporting more than one state at once. Takes a SUMMARY
 * (`{ unread, flagged, urgent }`) reported upward by ClientEmailDisplay once
 * its fetch lands, rather than an array — the list is lazy, so nothing
 * upstream of that component can know these numbers.
 *
 * Flag levels reuse the ladder the list rows already paint with: `!` is
 * `email-item-important` (warning), `!!` is `email-item-urgent` (danger).
 * Urgent is pushed first because the tile renders them in order.
 */
export const emailTile = (summary) => {
  if (!summary) return { ...NONE };

  const urgent = summary.urgent || 0;
  const flagged = summary.flagged || 0;
  const unread = summary.unread || 0;

  const statuses = [];
  if (urgent > 0) {
    statuses.push({ tone: "danger", value: urgent, text: `${urgent} urgent` });
  }
  if (flagged > 0) {
    statuses.push({
      tone: "warning",
      value: flagged,
      text: `${flagged} flagged`,
    });
  }
  if (unread > 0) {
    statuses.push({ tone: "accent", value: unread, text: `${unread} unread` });
  }

  return { count: undefined, statuses };
};

/** Activity has no resolver yet — nothing to report, by design. */
export const activityTile = () => ({ ...NONE });

/*
 * Named exports only — consumers do `import * as sectionSelect`, and a default
 * object here would just be a second way to reach the same functions.
 */
